/**
 * Rate Limiter Client Library
 *
 * Prevents scan spam and abuse
 * Poka-Yoke: Multiple layers of protection against excessive scanning
 */

export interface RateLimitConfig {
  maxAttempts: number;
  windowMs: number;
  storageKey: string;
}

export interface RateLimitEntry {
  timestamp: number;
  type: "scan" | "search" | "api";
}

/**
 * Rate Limiter for client-side actions
 * Stores attempts in memory and localStorage for persistence
 */
export class RateLimiter {
  private attempts: RateLimitEntry[] = [];
  private config: RateLimitConfig;

  constructor(config: RateLimitConfig) {
    this.config = config;
    this.loadFromStorage();
  }

  /**
   * Check if action can proceed
   * Poka-Yoke: Prevents abuse before it happens
   */
  canAttempt(type: "scan" | "search" | "api" = "scan"): boolean {
    this.cleanup();

    // Count attempts of this type in current window
    const now = Date.now();
    const recentAttempts = this.attempts.filter(
      (entry) => entry.type === type && now - entry.timestamp < this.config.windowMs
    );

    return recentAttempts.length < this.config.maxAttempts;
  }

  /**
   * Record an attempt
   */
  recordAttempt(type: "scan" | "search" | "api" = "scan"): void {
    const now = Date.now();
    this.attempts.push({ timestamp: now, type });
    this.saveToStorage();
    this.cleanup();
  }

  /**
   * Get remaining attempts
   */
  getRemainingAttempts(type: "scan" | "search" | "api" = "scan"): number {
    this.cleanup();

    const now = Date.now();
    const recentAttempts = this.attempts.filter(
      (entry) => entry.type === type && now - entry.timestamp < this.config.windowMs
    );

    return Math.max(0, this.config.maxAttempts - recentAttempts.length);
  }

  /**
   * Get time until next attempt allowed (in milliseconds)
   */
  getTimeUntilReset(type: "scan" | "search" | "api" = "scan"): number {
    this.cleanup();

    if (this.canAttempt(type)) {
      return 0;
    }

    const now = Date.now();
    const oldestAttempt = this.attempts
      .filter((entry) => entry.type === type)
      .sort((a, b) => a.timestamp - b.timestamp)[0];

    if (!oldestAttempt) {
      return 0;
    }

    const resetTime = oldestAttempt.timestamp + this.config.windowMs;
    return Math.max(0, resetTime - now);
  }

  /**
   * Reset all attempts (for testing or admin override)
   */
  reset(): void {
    this.attempts = [];
    this.saveToStorage();
  }

  /**
   * Clean up old attempts outside the window
   */
  private cleanup(): void {
    const now = Date.now();
    this.attempts = this.attempts.filter((entry) => now - entry.timestamp < this.config.windowMs);
    this.saveToStorage();
  }

  /**
   * Load attempts from localStorage for persistence
   */
  private loadFromStorage(): void {
    if (typeof window === "undefined") {
      return;
    }

    try {
      const stored = localStorage.getItem(this.config.storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          this.attempts = parsed;
          this.cleanup(); // Clean up old entries on load
        }
      }
    } catch (error) {
      console.warn("[RateLimiter] Failed to load from storage:", error);
      this.attempts = [];
    }
  }

  /**
   * Save attempts to localStorage
   */
  private saveToStorage(): void {
    if (typeof window === "undefined") {
      return;
    }

    try {
      localStorage.setItem(this.config.storageKey, JSON.stringify(this.attempts));
    } catch (error) {
      console.warn("[RateLimiter] Failed to save to storage:", error);
    }
  }
}

/**
 * Pre-configured rate limiters for common use cases
 */

// Barcode scan rate limiter: 20 scans per minute
export const scanRateLimiter = new RateLimiter({
  maxAttempts: 20,
  windowMs: 60000, // 1 minute
  storageKey: "radiomisr_scan_rate_limit",
});

// Search rate limiter: 30 searches per minute
export const searchRateLimiter = new RateLimiter({
  maxAttempts: 30,
  windowMs: 60000, // 1 minute
  storageKey: "radiomisr_search_rate_limit",
});

// API call rate limiter: 50 calls per minute
export const apiRateLimiter = new RateLimiter({
  maxAttempts: 50,
  windowMs: 60000, // 1 minute
  storageKey: "radiomisr_api_rate_limit",
});

/**
 * Format time remaining in Arabic
 */
export function formatTimeRemaining(milliseconds: number): string {
  const seconds = Math.ceil(milliseconds / 1000);

  if (seconds < 60) {
    return `${seconds} ثانية`;
  }

  const minutes = Math.ceil(seconds / 60);
  return `${minutes} دقيقة`;
}
